import { useMutation, useQuery } from '@tanstack/react-query';
import { type ReactElement, useEffect, useMemo, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { getBuildById } from '@/features/builds/data/builds';
import { querySuiteExecutions } from '@/features/executions/data/suiteExecutions';
import { deleteTestExecution } from '@/features/executions/data/testExecutions';
import { useProjectFilter } from '@/features/projects';
import { NotFoundScreen } from '@/pages/NotFoundScreen';
import { useNotifications } from '@/providers/notificationHooks';
import {
  gridPaths,
  invalidateGridQuery,
  invalidateTestExecutions,
  queryKeys,
  useAccessToken,
  useInvalidateQueries,
} from '@/shared/hooks';
import { buildEqFilter } from '@/ui/composites/grid/gridFilters';
import { DetailPageScaffold } from '@/ui/composites/scaffolds';
import { usePageTitle } from '@/ui/hooks/usePageTitle';
import { useBreadcrumbs } from '@/ui/layout/breadcrumbsContext';
import { useTestExecutionDetailQuery } from '../hooks/executions';
import { executionKeys } from '../queries/executionsQueryKeys';
import { TestExecutionDetailContent } from './TestExecutionDetailContent';
import type { AuthSession } from '@/auth/types';
import type { ApiError } from '@/shared/data/client';
import type { BuildDto } from '@flowqa/shared';

type TestExecutionDetailPageProps = { session: AuthSession | null };

export const TestExecutionDetailPage = ({
  session,
}: TestExecutionDetailPageProps): ReactElement => {
  const { testSlug, runNumber } = useParams<{ testSlug: string; runNumber: string }>();
  const navigate = useNavigate();
  const { selectedProjectId, setSelectedProjectId } = useProjectFilter();
  const token = useAccessToken();
  const { notify } = useNotifications();
  const invalidateQueries = useInvalidateQueries();
  const { setOverrideItems, clearOverride } = useBreadcrumbs();
  const resolvedSlug = useMemo(() => (testSlug ?? '').trim(), [testSlug]);
  const lastBreadcrumbKey = useRef<string | null>(null);
  const parsedRunNumber = useMemo(() => {
    const parsed = Number(runNumber);
    return Number.isFinite(parsed) ? parsed : null;
  }, [runNumber]);
  const executionQuery = useTestExecutionDetailQuery(
    resolvedSlug || null,
    parsedRunNumber,
    selectedProjectId ?? null,
  );
  const { data: execution, isPending, isError, error } = executionQuery;
  usePageTitle(execution?.name ?? execution?.testName ?? 'Test Execution');

  const buildId = execution?.resultsFile?.buildId ?? null;
  const buildQuery = useQuery<BuildDto | null, ApiError>({
    queryKey: queryKeys.buildDetail(buildId, execution?.organisationId ?? null),
    enabled: Boolean(token && buildId),
    queryFn: async () =>
      buildId ? await getBuildById(buildId, execution?.organisationId ?? undefined) : null,
  });
  const build = buildQuery.data ?? null;
  const suiteExecutionId = execution?.suiteExecutionId ?? null;
  const suiteExecutionRunNumberQuery = useQuery<number | null, ApiError>({
    queryKey: executionKeys.suiteExecutionRunNumber(
      suiteExecutionId,
      execution?.organisationId ?? null,
    ),
    enabled: Boolean(token && suiteExecutionId),
    queryFn: async () =>
      suiteExecutionId
        ? ((
            await querySuiteExecutions(
              execution?.projectId ?? null,
              execution?.organisationId ?? null,
              {
                page: 1,
                pageSize: 1,
                sorts: [
                  { field: 'timestamp', direction: 'asc' },
                  { field: 'id', direction: 'asc' },
                ],
                filters: buildEqFilter('id', suiteExecutionId),
              },
            )
          ).items[0]?.runNumber ?? null)
        : null,
  });
  const suiteExecutionRunNumber = suiteExecutionRunNumberQuery.data ?? null;

  const deleteTestExecutionMutation = useMutation<{ id: string }, ApiError, { id: string }>({
    mutationFn: async (input) =>
      await deleteTestExecution({ id: input.id }, execution?.organisationId ?? null),
    onSuccess: async () => {
      await invalidateQueries([
        invalidateTestExecutions(selectedProjectId ?? null, execution?.organisationId ?? null),
        invalidateGridQuery(gridPaths.testExecutions),
      ]);
      notify({ message: 'Test execution deleted.', tone: 'green' });
      const fallbackHref = resolvedSlug ? `/tests/${resolvedSlug}` : '/tests';
      void navigate(fallbackHref, { replace: true });
    },
    onError: (deleteError) => {
      notify({ message: deleteError.message, tone: 'red' });
    },
  });

  const hasInvalidParams = !resolvedSlug || parsedRunNumber === null;
  const showNotFound = hasInvalidParams || (!isPending && executionQuery.isFetched && !execution);

  useEffect(() => {
    if (!execution?.projectId) return;
    if (execution.projectId !== selectedProjectId) {
      setSelectedProjectId(execution.projectId);
    }
  }, [execution, selectedProjectId, setSelectedProjectId]);

  useEffect(() => {
    if (!execution) return () => undefined;
    const nameLabel = execution.testName ?? execution.name ?? 'Test';
    const runLabel =
      execution.runNumber !== null && execution.runNumber !== undefined
        ? `Run ${execution.runNumber}`
        : 'Execution';
    const nextKey = `${execution.id}:${nameLabel}:${runLabel}`;
    if (lastBreadcrumbKey.current === nextKey) return () => undefined;
    lastBreadcrumbKey.current = nextKey;
    const testHref = resolvedSlug ? `/tests/${resolvedSlug}` : '/tests';
    setOverrideItems([
      { label: 'Tests', href: '/tests' },
      { label: nameLabel, href: testHref },
      { label: runLabel },
    ]);
    return () => {
      clearOverride();
      lastBreadcrumbKey.current = null;
    };
  }, [clearOverride, execution, resolvedSlug, setOverrideItems]);

  if (showNotFound) {
    return (
      <NotFoundScreen
        title="Execution not found"
        body="This test execution does not exist or you no longer have access to it."
        backTo="/tests"
        backLabel="Back to tests"
      />
    );
  }

  const handleDeleteTestExecution = async (): Promise<void> => {
    if (!execution) return;
    await deleteTestExecutionMutation.mutateAsync({ id: execution.id });
  };

  return (
    <DetailPageScaffold
      session={session}
      sessionMessage="Sign in to view test execution details."
      requireProject={false}
      projectMessage="Select a project to view test execution details."
      projectId={selectedProjectId}
      isPending={isPending}
      isError={isError}
      error={error}
      errorMessage="Failed to load execution"
      data={execution}
    >
      {(resolvedExecution) => (
        <TestExecutionDetailContent
          title={
            resolvedExecution.runNumber !== null && resolvedExecution.runNumber !== undefined
              ? {
                  name: resolvedExecution.name ?? resolvedExecution.testName ?? 'Test Execution',
                  runNumber: resolvedExecution.runNumber,
                }
              : (resolvedExecution.name ?? resolvedExecution.testName ?? 'Test Execution')
          }
          execution={resolvedExecution}
          build={build}
          suiteExecutionRunNumber={suiteExecutionRunNumber}
          onDelete={handleDeleteTestExecution}
          deleteLoading={deleteTestExecutionMutation.isPending}
        />
      )}
    </DetailPageScaffold>
  );
};
